import { useQuery } from "@tanstack/react-query";
import { baseUrl, token } from "api";
import { API_ENDPOINTS } from "../apiEndPoints";
import axios from "axios";

const fetchProductsByCategory = async ({ queryKey }: any) => {
  const categoryIds = queryKey[1]?.categoryIds;
  const page = queryKey[1]?.page ?? 1;
  
  if (!categoryIds || categoryIds.length === 0) {
    return null;
  }
  
  // If single category, fetch directly
  if (categoryIds.length === 1) {
    const response = await axios.get(
      `${baseUrl}${API_ENDPOINTS.ProductByCategory}/${categoryIds[0]}?page=${page}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } else {
    // For multiple categories, fetch from each category and combine results
    try {
      const promises = categoryIds.map((categoryId: string) =>
        axios.get(
          `${baseUrl}${API_ENDPOINTS.ProductByCategory}/${categoryId}?page=${page}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
      );
      
      const responses = await Promise.all(promises);
      
      // Combine all products from different categories
      const allProducts: any[] = [];
      const allMeta = responses[0]?.data?.meta; // Use meta from first response
      
      responses.forEach((response) => {
        if (response.data?.data) {
          allProducts.push(...response.data.data);
        }
      });
      
      // Remove duplicates based on product ID
      const uniqueProducts = allProducts.filter((product, index, self) =>
        index === self.findIndex((p) => p.id === product.id)
      );
      
      return {
        data: uniqueProducts,
        meta: allMeta
      };
    } catch (error) {
      console.error('Error fetching products from multiple categories:', error);
      // Fallback to first category if there's an error
      const response = await axios.get(
        `${baseUrl}${API_ENDPOINTS.ProductByCategory}/${categoryIds[0]}?page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    }
  }
};

export const useProductsByCategory = (categoryIds: string[], page: number = 1) => {
  const { data: ProductsByCategoryData, isLoading } = useQuery({
    queryKey: ["ProductsByCategory", { categoryIds, page }],
    queryFn: fetchProductsByCategory,
    staleTime: 1000 * 60 * 5,
    enabled: categoryIds.length > 0, // Only run query if categoryIds are provided
  });

  return { ProductsByCategoryData, isLoading };
};
